import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { distinctUntilChanged, filter } from 'rxjs';
import { DataService } from 'src/core/services/data.service';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { PsmSiteResponse } from 'src/core/types/servicehistory.types';
import { Psm, PSMData } from 'src/core/types/systemhealth.types';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-psm-pattern-details',
  imports: [CommonModule, TranslocoModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './psm-pattern-details.component.html',
  styleUrl: './psm-pattern-details.component.css'
})
export class PsmPatternDetailsComponent {

  patternData: Psm;


  data: { correct_actions: string, metadata: any, pattern_id: string, problem_causes: string, rule_description: string, technical_description: string } = {
    correct_actions: '',
    metadata: {},
    pattern_id: '',
    problem_causes: '',
    rule_description: '',
    technical_description: ''
  };
  pattern: string;
  serial: string;
  progressInfo: any;

  constructor(private http: HttpClient, private dataservice: DataService, private selectedSystemService: SelectedsystemService, private selectedContentService: SelectedContentService, private translocoService: TranslocoService) { }



  ngOnInit(): void {

    this.selectedSystemService.$selectedPattern.pipe(
      filter((pattern): pattern is string => !!pattern),
      distinctUntilChanged()
    ).subscribe(pattern => {
      this.pattern = pattern;
      this.ensureProasAvailable(pattern);
      this.loadPatternDetails(pattern);
    });

    this.selectedSystemService.$proas.subscribe((x: PSMData | PsmSiteResponse) => {
      this.applyPatternData(x);
    });
  }

  private ensureProasAvailable(pattern: string): void {
    const cached = this.selectedSystemService.$proas.value;
    if (this.hasPattern(cached, pattern)) {
      this.applyPatternData(cached);
      return;
    }

    const isDevice = this.selectedContentService.selection.kind === 'device';
    const serial = this.selectedContentService.currentSystem?.SerialNumber || '';
    const material = this.selectedContentService.currentSystem?.MaterialNumber || '';
    const funcloc = this.selectedContentService.currentSite?.Id;

    if (isDevice) {
      this.dataservice.getPSMs(material, serial).subscribe({
        next: ({ response, status }) => {
          this.selectedSystemService.$proas.next(status === 200 ? response : null);
        },
        error: (err) => {
          console.error('Failed to fetch PSM list for pattern modal', err);
          this.selectedSystemService.$proas.next(null);
        }
      });
      return;
    }

    this.dataservice.getPSMsSite(funcloc).subscribe({
      next: ({ response, status }) => {
        this.selectedSystemService.$proas.next(status === 200 ? response : null);
      },
      error: (err) => {
        console.error('Failed to fetch PSM list for pattern modal', err);
        this.selectedSystemService.$proas.next(null);
      }
    });
  }

  private applyPatternData(x: PSMData | PsmSiteResponse): void {
    if (!x || !this.pattern) {
      this.patternData = null;
      this.progressInfo = null;
      this.serial = null;
      return;
    }

    if (this.selectedContentService.selection.kind === 'device') {
      const temp = x as PSMData;
      const data = temp?.psms?.find((p: any) => p.pattern_id === this.pattern) as Psm;
      this.patternData = data ?? null;
      this.progressInfo = data ? this.calculateRemainingDays(data.target_date, data.status, 0) : null;
      this.serial = temp?.serial_number || null;
      return;
    }

    const temp = x as PsmSiteResponse;
    const mapped = temp?.psms
      ? temp.psms || []
      : [];

    const matched = mapped.find((p: any) => p.pattern_id === this.pattern);
    const data = matched as Psm;
    this.patternData = data ?? null;
    this.progressInfo = data ? this.calculateRemainingDays(data.target_date, data.status, 0) : null;
    this.serial = matched?.serial_number || null;
  }

  private hasPattern(x: PSMData | PsmSiteResponse, pattern: string): boolean {
    if (!x || !pattern) {
      return false;
    }

    if (this.selectedContentService.selection.kind === 'device') {
      const temp = x as PSMData;
      return !!temp?.psms?.some((p: any) => p.pattern_id === pattern);
    }

    const temp = x as PsmSiteResponse;
    return !!(temp.psms || []).some((psm: any) => psm.pattern_id === pattern);
    
  }

  private loadPatternDetails(pattern: string): void {
    this.dataservice.getPSMDetails(pattern).subscribe({
      next: (response: any) => {
        const payload = response?.response;
        if (!payload) {
          return;
        }

        this.data = payload;

        const actions = this.data.correct_actions || '';
        let formatted = this.getFormattedText(actions);
        const problem_causes = this.data.problem_causes || '';
        this.data.correct_actions = formatted.trim();
        let formattedCauses = this.getFormattedText(problem_causes);
        this.data.problem_causes = formattedCauses.trim();
      },
      error: (err) => {
        console.error('Failed to fetch PSM details', err);
      }
    });
  }

  calculateRemainingDays = (inputDay: string, status: string, urgency: number) => {

    const inputDate = new Date(inputDay);
    const today = new Date();

    // Set the time of both dates to midnight to get only the date part
    inputDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    // Calculate the time difference in milliseconds
    const timeDifference = inputDate.getTime() - today.getTime();

    // Convert milliseconds to days
    const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

    let string;
    let statusString;
    //let closeDate = this.data.filter(f => f.NOTI_ID === this.alarm[0].NOTIFICATION_NUM)[0]?.NOTI_CLOSE_DT;

    if (status !== "Resolved") {
      if (Math.round(daysDifference) < 0) {
        string = this.translocoService.translate('psmDetails.pattern.overdueSince', { days: Math.abs(Math.round(daysDifference)) });
        statusString = "error";
      }
      else {
        string = this.translocoService.translate('psmDetails.pattern.daysRemaining', { days: Math.round(daysDifference) });
        statusString = "warning";
      }
    }
    else {
      string = this.translocoService.translate('psmDetails.pattern.resolved');
      statusString = "success";
    }

    //let quotient = 100 / urgency;

    let progress = Math.round(daysDifference);

    // let resolution = closeDate ? "Resolved on "+closeDate.substring(0,10) : "";


    return [string, statusString, progress]
  }

  private getFormattedText(actions: string) {
    const segments = actions.split('|').map(s => s.trim()).filter(s => s.length > 0);
    let formatted = '';
    segments.forEach(seg => {
      if (seg.match(/steps:$/i)) {
        // Section header
        formatted += (formatted ? '\n\n' : '') + seg + '\n';
      } else {
        // Bullet point
        formatted += `• ${seg}\n`;
      }
    });
    return formatted;
  }

  ngOnDestroy(): void {
    this.patternData = null;
  }
}
